import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useCalendarEvents, CalendarEvent } from "@/hooks/useCalendarEvents";
import { CalendarSidebar, getInitialLayers, saveLayers } from "@/components/controller/calendar/CalendarSidebar";
import { CalendarMonthView } from "@/components/controller/calendar/CalendarMonthView";
import { CalendarWeekView } from "@/components/controller/calendar/CalendarWeekView";
import { CalendarListView } from "@/components/controller/calendar/CalendarListView";
import { NewEventDialog } from "@/components/controller/calendar/NewEventDialog";
import { EventPreviewDialog } from "@/components/controller/calendar/EventPreviewDialog";
import { EditEventDialog } from "@/components/controller/calendar/EditEventDialog";
import { cn } from "@/lib/utils";
import { addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, format, startOfWeek, endOfWeek } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

/** Parse YYYY-MM-DD into a local Date (no timezone shift). */
const parseLocalDate = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return isNaN(d.getTime()) ? null : d;
};


type ViewMode = "month" | "week" | "day" | "list";

const Calendar = () => {
  const isMobile = useIsMobile();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? "list" : "week");
  const [layers, setLayers] = useState<Record<string, boolean>>(getInitialLayers);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventDefaultDate, setNewEventDefaultDate] = useState<Date>(new Date());

  // Preview & Edit state
  const [previewEvent, setPreviewEvent] = useState<CalendarEvent | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Deep-link from notifications: ?date=YYYY-MM-DD&view=day&eventId=<uuid>
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightEventId, setHighlightEventId] = useState<string | null>(null);
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);

  const { data: events = [], isLoading } = useCalendarEvents();

  // Apply date + view from URL on mount.
  useEffect(() => {
    const dateStr = searchParams.get("date");
    const viewStr = searchParams.get("view");
    const evId = searchParams.get("eventId");
    let consumed = false;

    if (dateStr) {
      const parsed = parseLocalDate(dateStr);
      if (parsed) {
        setSelectedDate(parsed);
        setCurrentMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
        consumed = true;
      }
    }

    const validViews: ViewMode[] = ["month", "week", "day", "list"];
    if (viewStr && (validViews as string[]).includes(viewStr)) {
      setViewMode(viewStr as ViewMode);
      consumed = true;
    } else if (dateStr) {
      setViewMode("day");
    }

    if (evId) {
      setPendingEventId(evId);
      setHighlightEventId(evId);
      consumed = true;
    }

    if (consumed) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open preview once events arrive.
  useEffect(() => {
    if (!pendingEventId || events.length === 0) return;
    const found = events.find((e) => e.id === pendingEventId);
    if (found) {
      setPreviewEvent(found);
      setPreviewOpen(true);
    }
    setPendingEventId(null);
  }, [pendingEventId, events]);

  // Auto-clear highlight after 5s.
  useEffect(() => {
    if (!highlightEventId) return;
    const t = setTimeout(() => setHighlightEventId(null), 5000);
    return () => clearTimeout(t);
  }, [highlightEventId]);


  const handleLayerToggle = useCallback((key: string, checked: boolean) => {
    setLayers((prev) => {
      const next = { ...prev, [key]: checked };
      saveLayers(next);
      return next;
    });
  }, []);

  const handleDayClick = useCallback((d: Date) => {
    setSelectedDate(d);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setViewMode("day");
  }, []);

  const handleEmptyCellClick = useCallback((d: Date) => {
    setNewEventDefaultDate(d);
    setNewEventOpen(true);
  }, []);

  const handleEventPreview = useCallback((ev: CalendarEvent) => {
    setPreviewEvent(ev);
    setPreviewOpen(true);
  }, []);

  const handleEventEdit = useCallback((ev: CalendarEvent) => {
    setEditEvent(ev);
    setEditOpen(true);
  }, []);

  // ── Period-aware navigation ─────────────────────────────────────────
  const goPrev = useCallback(() => {
    if (viewMode === "month") setCurrentMonth((m) => subMonths(m, 1));
    else if (viewMode === "week") setSelectedDate((d) => { const n = subWeeks(d, 1); setCurrentMonth(new Date(n.getFullYear(), n.getMonth(), 1)); return n; });
    else if (viewMode === "day") setSelectedDate((d) => { const n = subDays(d, 1); setCurrentMonth(new Date(n.getFullYear(), n.getMonth(), 1)); return n; });
    else setCurrentMonth((m) => subMonths(m, 1));
  }, [viewMode]);

  const goNext = useCallback(() => {
    if (viewMode === "month") setCurrentMonth((m) => addMonths(m, 1));
    else if (viewMode === "week") setSelectedDate((d) => { const n = addWeeks(d, 1); setCurrentMonth(new Date(n.getFullYear(), n.getMonth(), 1)); return n; });
    else if (viewMode === "day") setSelectedDate((d) => { const n = addDays(d, 1); setCurrentMonth(new Date(n.getFullYear(), n.getMonth(), 1)); return n; });
    else setCurrentMonth((m) => addMonths(m, 1));
  }, [viewMode]);

  const goToday = useCallback(() => {
    const now = new Date();
    setSelectedDate(now);
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  // ── Title based on view mode ────────────────────────────────────────
  const title = useMemo(() => {
    if (viewMode === "month") return format(currentMonth, "MMMM yyyy");
    if (viewMode === "week") {
      const weekStart = startOfWeek(selectedDate);
      const weekEnd = endOfWeek(selectedDate);
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${format(weekStart, "MMM d")} – ${format(weekEnd, "d, yyyy")}`;
      }
      return `${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`;
    }
    if (viewMode === "day") return format(selectedDate, "EEEE, MMMM d, yyyy");
    return format(currentMonth, "MMMM yyyy");
  }, [viewMode, currentMonth, selectedDate]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      switch (e.key.toLowerCase()) {
        case "t": goToday(); break;
        case "m": setViewMode("month"); break;
        case "w": setViewMode("week"); break;
        case "d": setViewMode("day"); break;
        case "n": setNewEventDefaultDate(selectedDate); setNewEventOpen(true); break;
        case "j": case "arrowleft": goPrev(); break;
        case "k": case "arrowright": goNext(); break;
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [goToday, goPrev, goNext, selectedDate]);

  return (
    <div className="flex h-full overflow-hidden bg-background">
      {/* Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <CalendarSidebar
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
          onDayClick={handleDayClick}
          selectedDate={selectedDate}
          layers={layers}
          onLayerToggle={handleLayerToggle}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <div className="flex flex-wrap items-center justify-between gap-1 p-2 md:p-3 border-b bg-card shrink-0">
          <div className="flex items-center gap-1 md:gap-2">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={goPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xs md:text-sm font-semibold min-w-[100px] md:min-w-[180px] text-center">{title}</h2>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={goNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7 ml-1 md:ml-2" onClick={goToday}>
              Today
            </Button>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            <div className="flex bg-muted rounded-md p-0.5">
              {(["month", "week", "day", "list"] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={cn(
                    "px-2 md:px-2.5 py-1 text-[10px] md:text-xs rounded capitalize transition-colors",
                    viewMode === mode
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {mode}
                </button>
              ))}
            </div>
            <Button size="sm" className="h-7 text-xs gap-1" onClick={() => { setNewEventDefaultDate(selectedDate); setNewEventOpen(true); }}>
              <Plus className="h-3 w-3" />
              <span className="hidden sm:inline">Add Event</span>
            </Button>
          </div>
        </div>

        {/* Calendar view */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Loading events…
          </div>
        ) : viewMode === "month" ? (
          <CalendarMonthView
            month={currentMonth}
            events={events}
            layers={layers}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
            onEmptyCellClick={handleEmptyCellClick}
            onEventPreview={handleEventPreview}
            onEventEdit={handleEventEdit}
            highlightEventId={highlightEventId}
          />
        ) : viewMode === "week" ? (
          <CalendarWeekView
            selectedDate={selectedDate}
            events={events}
            layers={layers}
            onDayClick={handleDayClick}
            onEmptyCellClick={handleEmptyCellClick}
            onEventPreview={handleEventPreview}
            onEventEdit={handleEventEdit}
            highlightEventId={highlightEventId}
          />
        ) : viewMode === "day" ? (
          <CalendarWeekView
            selectedDate={selectedDate}
            events={events}
            layers={layers}
            dates={[selectedDate]}
            onEmptyCellClick={handleEmptyCellClick}
            onEventPreview={handleEventPreview}
            onEventEdit={handleEventEdit}
            highlightEventId={highlightEventId}
          />
        ) : (
          <CalendarListView selectedDate={selectedDate} events={events} layers={layers} />
        )}
      </div>

      <NewEventDialog
        open={newEventOpen}
        onOpenChange={setNewEventOpen}
        defaultDate={newEventDefaultDate}
      />

      <EventPreviewDialog
        event={previewEvent}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        onEdit={handleEventEdit}
      />

      <EditEventDialog
        event={editEvent}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </div>
  );
};

export default Calendar;
